import * as React from 'react';
import styled from 'styled-components';
import { ContentSpacing } from '@estee/elc-base-theme';
import { Body1, Body2 } from '@estee/elc-typography';
import { observer } from 'mobx-react';

import { IShade, ISku } from '../../interfaces/IVto';
import { ProductPriceFormatted } from '../common/ProductPriceFormatted';
import { VtoShadeName } from '../common/VtoShadeName';
import { classes } from '../../utils/Classes';
import {
    VTO_PRODUCT_NAME,
    VTO_PRODUCT_SIZE,
    VTO_PRODUCT_SUBHEADER
} from '../../constants/DataTestIdAttributes';
import { TaxIncluded } from '../common/TaxIncluded';
import { VtoProductPrice } from './VtoProductPrice';
import { config } from '@estee/elc-service';

export interface IVtoShadeInfoLeft {
    displayName: string;
    subHeader?: string;
    selectedSku: ISku;
    selectedShade: IShade;
    selectedSizeValue?: string;
    formattedPrice?: string;
    formattedOriginalPrice?: string;
    formattedPricePerUnit?: string;
}

interface Props extends IVtoShadeInfoLeft {
    hideProductPricePerUnit?: boolean;
    hideProductPrice: boolean;
    config: {
        showIncludeTaxLabel: boolean;
    };
}

const Wrapper = styled.div`
    float: left;
    padding-left: ${ContentSpacing.space12};
    width: 50%;
    [dir='rtl'] & {
        float: right;
        padding-right: ${ContentSpacing.space12};
        padding-left: 0;
    }
`;

const shadeInfoLeftContainerClassNames = classes('vto-shade-info-left-container');
const productClassNames = classes('vto-product-name');
const productSubHeaderClassNames = classes('vto-sub-header');
const productPriceClassNames = classes('vto-price');
const productSizeClassNames = classes('vto-product-size');

export const VtoShadeInfoLeftContainer = config(['showIncludeTaxLabel'])(
    observer(
        ({
            displayName,
            subHeader,
            selectedShade,
            selectedSizeValue,
            hideProductPricePerUnit,
            hideProductPrice,
            selectedSku: { formattedPrice, formattedOriginalPrice, formattedPricePerUnit, prices },
            config: { showIncludeTaxLabel }
        }: Props) => (
            <Wrapper className={shadeInfoLeftContainerClassNames}>
                <Body1 className={productClassNames} data-test-id={VTO_PRODUCT_NAME}>
                    {displayName}
                </Body1>
                <Body2 className={productSubHeaderClassNames} data-test-id={VTO_PRODUCT_SUBHEADER}>
                    {subHeader}
                </Body2>
                {selectedShade && <VtoShadeName selectedShade={selectedShade} />}
                {!hideProductPrice && (
                    <>
                        {showIncludeTaxLabel && <TaxIncluded />}
                        <div className={productPriceClassNames}>
                            <ProductPriceFormatted
                                formattedPrice={formattedPrice}
                                formattedOriginalPrice={formattedOriginalPrice}
                            />
                            {!hideProductPricePerUnit && (
                                <VtoProductPrice
                                    formattedPricePerUnit={formattedPricePerUnit}
                                    prices={prices}
                                />
                            )}
                        </div>
                    </>
                )}
                {selectedSizeValue && (
                    <Body2 className={productSizeClassNames} data-test-id={VTO_PRODUCT_SIZE}>
                        {selectedSizeValue}
                    </Body2>
                )}
            </Wrapper>
        )
    )
);
